import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SubscribeForm from "@/components/feature/SubscribeForm";
import { useSeo } from "@/hooks/useSeo";

const BASE = "https://secretcomix.github.io";

const principles = [
  {
    icon: "ri-heart-3-fill",
    title: "只选治愈的",
    text: "我们的选品标准始终如一：轻松、好看、看完心里暖暖的。拒绝刀与虐，只推能让碎片时间变美好的作品。",
  },
  {
    icon: "ri-eye-fill",
    title: "人工把关",
    text: "每一部上榜作品都由编辑团队亲自翻阅、亲自试读，而不是只看热度榜。宁缺毋滥，追更不踩雷。",
  },
  {
    icon: "ri-focus-3-fill",
    title: "聚焦萌系",
    text: "我们专注萌系日常这一细分方向，深耕治愈番、搞笑萌漫、软萌画风与温馨向轻喜剧，把一件事做到专业。",
  },
  {
    icon: "ri-user-heart-fill",
    title: "陪你放松",
    text: "我们相信内容是用来治愈生活的。无论多忙，喵趣都希望成为你每天花几分钟就能获得快乐的地方。",
  },
];

const testimonials = [
  {
    name: "阿茶",
    tag: "通勤党",
    text: "每天地铁上点开喵趣，五分钟就能被治愈到。以前总觉得没时间看番，现在碎片时间反而成了我一天最期待的时候。",
  },
  {
    name: "栗子",
    tag: "深夜写稿人",
    text: "编辑部推荐的《午后的窗边猫》直接把我锁死了。这个站是真的懂治愈，每一部都是暖到心里的那种。",
  },
  {
    name: "小满",
    tag: "漫画萌新",
    text: "第一次入坑萌系漫画就是从这里开始的，分类很清楚，评分也很靠谱，跟着推荐追了半年没有踩过一次雷。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 喵趣漫画网的定位、理念与口碑",
    description:
      "了解喵趣漫画网：一个专注萌系日常、治愈番、搞笑萌漫、软萌画风与温馨向轻喜剧的内容分享平台。看看我们的选品理念、内容原则与读者口碑。",
    keywords: "喵趣漫画网,关于我们,萌系日常,治愈番,网站理念,读者口碑",
    url: BASE + "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于喵趣漫画网",
        description: "喵趣漫画网是一个专注萌系日常与治愈番的内容分享平台。",
        url: BASE + "/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "喵趣漫画网",
        url: BASE + "/",
        description: "专注萌系日常与治愈番的漫画推荐平台",
      },
    ],
  });

  return (
    <Layout>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=wide%20soft%20illustration%20of%20a%20cozy%20reading%20corner%20with%20cats%2C%20plants%20and%20stacked%20manga%20books%20under%20warm%20sunlight%2C%20pastel%20peach%20cream%20and%20mint%20palette%2C%20gentle%20cute%20storybook%20vector%20art%2C%20healing%20and%20peaceful%20mood&width=1600&height=700&seq=about-hero-v1&orientation=landscape"
            alt="喵趣漫画网关于我们页主题插画"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/90 via-background-50/60 to-background-50"></div>
        </div>
        <div className="container-page relative z-10 flex w-full flex-col items-center py-16 text-center md:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-background-50/80 px-4 py-1.5 text-xs font-bold text-primary-700 backdrop-blur">
            <i className="ri-heart-3-fill text-sm" aria-hidden="true"></i>
            关于喵趣漫画网
          </span>
          <h1 className="mt-5 max-w-3xl text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            用治愈的作品，陪你度过每一段碎片时光
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-foreground-700 md:text-base">
            喵趣漫画网诞生于一个朴素的想法：在信息爆炸的年代，找一部真正放松、真正治愈的作品并不容易。
            于是我们决定做这样一个地方——只收录萌系日常，只推荐好看到上头的治愈番、搞笑萌漫、软萌画风与温馨向轻喜剧。
          </p>
        </div>
      </section>

      <section className="container-page mt-10 md:mt-14">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-center">
          <div>
            <span className="chip bg-primary-100 text-primary-700">
              <i className="ri-quill-pen-fill mr-1" aria-hidden="true"></i>
              我们的定位
            </span>
            <h2 className="mt-4 text-xl font-bold text-foreground-950 md:text-2xl">内容分享平台，而不是又一个资讯噪声源</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              喵趣漫画网是一家专注萌系日常题材的内容分享平台。我们不做信息轰炸，也不追逐流量博眼球，
              只围绕「治愈、轻松、好看」这一件事持续深耕。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              从挑选标准到编辑推荐，我们都坚持为读者省心：你不用费脑挑选，点开就能开心。
              无论是通勤路上的五分钟、睡前的一点时间，还是想找部搞笑萌漫笑一笑，喵趣都能给你合适的答案。
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=cute%20illustration%20of%20a%20small%20team%20of%20cats%20working%20at%20a%20cozy%20desk%20with%20manga%20books%20and%20laptop%20and%20tea%2C%20warm%20lamp%20light%2C%20soft%20peach%20and%20cream%20palette%2C%20kawaii%20storybook%20vector%20art%2C%20portrait%20composition&width=1000&height=760&seq=about-story-v1&orientation=portrait"
              alt="喵趣漫画网编辑部日常插画"
              title="喵趣漫画网 - 编辑团队插画"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="mt-12 bg-background-100/60 py-12 md:mt-16 md:py-16">
        <div className="container-page">
          <div className="mx-auto max-w-xl text-center">
            <span className="chip bg-secondary-100 text-secondary-900">
              <i className="ri-shield-check-line mr-1" aria-hidden="true"></i>
              发展理念与内容原则
            </span>
            <h2 className="mt-4 text-xl font-bold text-foreground-950 md:text-2xl">我们坚持的四件事</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              以下四条，是喵趣从成立第一天起就刻在骨子里的原则，也是读者信任我们的原因。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-2xl border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-700">
                  <i className={p.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page mt-12 md:mt-16">
        <div className="mx-auto max-w-xl text-center">
          <span className="chip bg-primary-100 text-primary-700">
            <i className="ri-chat-3-fill mr-1" aria-hidden="true"></i>
            读者口碑
          </span>
          <h2 className="mt-4 text-xl font-bold text-foreground-950 md:text-2xl">大家这样说喵趣</h2>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-6">
              <i className="ri-double-quotes-l text-3xl text-primary-200" aria-hidden="true"></i>
              <blockquote className="mt-2 flex-1 text-sm leading-7 text-foreground-700">{t.text}</blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-100 pt-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-base font-bold text-secondary-900">
                  {t.name[0]}
                </span>
                <div>
                  <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.tag}</p>
                </div>
                <span className="ml-auto flex items-center gap-0.5 text-accent-500">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <i key={i} className="ri-star-fill text-xs" aria-hidden="true"></i>
                  ))}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mt-12 md:mt-16">
        <div className="container-page rounded-3xl border border-background-200 bg-background-50 px-6 py-12 text-center md:py-14">
          <span className="chip bg-accent-100 text-accent-800">
            <i className="ri-mail-send-line mr-1" aria-hidden="true"></i>
            联系我们
          </span>
          <h2 className="mt-5 text-2xl font-bold text-foreground-950 md:text-3xl">想投稿、合作，或聊聊你喜欢的漫画？</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-foreground-600">
            欢迎通过邮箱联系我们，或订阅频道，第一时间收到喵趣的治愈推荐与内容更新。
          </p>
          <p className="mt-5 flex items-center justify-center gap-2 text-sm font-medium text-foreground-800">
            <i className="ri-mail-line text-primary-600" aria-hidden="true"></i>
            hello@miaoqu-comics.example.com
          </p>
          <div className="mt-8">
            <SubscribeForm />
          </div>
          <Link to="/faq" className="btn-ghost mt-6">
            查看常见问题
          </Link>
        </div>
      </section>
    </Layout>
  );
}